<template>
  <v-container>
    <v-row v-if="depositFiles.length">
      <v-col class="ma-3">
        <div
          v-if="enableDelete && selected.length > 0"
          class="d-flex align-center mb-2"
        >
          <span class="text-body-2 mr-3">
            {{ selected.length }} file{{ selected.length !== 1 ? 's' : '' }} selected
          </span>
          <v-btn
            color="error"
            size="small"
            :loading="deleting"
            prepend-icon="mdi-delete-sweep"
            @click="deleteSelected"
          >
            Delete selected
          </v-btn>
        </div>
        <v-data-table
          v-model="selected"
          :items="depositFiles"
          :headers="headers"
          density="compact"
          show-select
          item-value="Key"
        >
          <template #[`item.Size`]="{ value }">
            {{ fileSize(value ?? 0) }}
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              v-if="enableDelete"
              icon
              variant="text"
              density="comfortable"
              :title="`Delete ${item.originalfilename}`"
              @click.stop="deleteItem(item)"
            >
              <v-icon color="error">
                mdi-delete
              </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row v-else>
      <p>There are no files in this deposit.</p>
    </v-row>
  </v-container>
</template>

<script>
import config from "@/config";
import { Auth } from '@/utils/amplify-compat';
import {
  S3Client,
  ListObjectsV2Command,
  HeadObjectCommand,
  DeleteObjectCommand
} from "@aws-sdk/client-s3";
import { filesize } from "filesize";
import { on, off, emit } from '@/utils/bus';

export default {
  name: "DepositFileList",
  props: {
    dialog: Boolean
  },
  emits: ['dirs', 'refresh', 'stats'],
  data: () => ({
    headers: [
      { title: 'Filename', key: 'originalfilename', sortable: true },
      { title: 'Folder',   key: 'folder',           sortable: true },
      { title: 'Size',     key: 'Size',              sortable: true },
      { title: 'Uploaded', key: 'LastModified',      sortable: true },
      { title: 'Checksum', key: 'checksum',          sortable: false },
      { title: 'Actions',  key: 'actions',           sortable: false, align: 'end' },
    ],
    depositFiles: [],
    selected: [],
    deleting: false,
    dirs: [{ name: 'Deposits', id: 1, children: [] }],
    metadata: {},
    activeDeposit: null,
    enableDelete: false
  }),
  created() {
    this.getData();
  },
  mounted() {
    on("refresh", this.getData);
  },
  beforeUnmount() {
    off("refresh", this.getData);
  },
  methods: {
    trigger() { emit("refresh", true); },
    fileSize(size) { return filesize(size) },

    // safer than slicing the path; router defines :deposit_id
    getActiveDeposit() {
      return this.$route.params.deposit_id;
    },

    async makeClient() {
      const creds = await Auth.currentCredentials();
      return {
        client: new S3Client({
          region: config.region,
          credentials: Auth.essentialCredentials(creds),
          // bucket has dots → force path-style avoids virtual-hostname issues
          forcePathStyle: true,
        }),
        identityId: creds.identityId,
      };
    },

    async listPrefix(client, Prefix) {
      const keys = [];
      let ContinuationToken;
      do {
        const out = await client.send(new ListObjectsV2Command({
          Bucket: config.bucketName,
          Prefix,
          ContinuationToken,
        }));
        keys.push(...(out.Contents || []).map(o => o.Key).filter(Boolean));
        ContinuationToken = out.IsTruncated ? out.NextContinuationToken : undefined;
      } while (ContinuationToken);
      return keys;
    },

    async getData() {
      this.selected = [];
      try {
        const creds = await Auth.currentCredentials();
        const client = new S3Client({
          region: config.region,
          credentials: Auth.essentialCredentials(creds),
          forcePathStyle: true, // bucket includes dots
        });
        // depositor comes via QUERY (?depositor=...), not params
        const depositor = this.$route.query.depositor || null;
        const identityId = depositor || creds.identityId;
        this.enableDelete = !depositor || depositor === creds.identityId;
        const depositId = this.getActiveDeposit();

        // Try both layouts
        const prefixes = [
          `private/${identityId}/deposits/${depositId}/`,
          `private/${identityId}/${depositId}/`,
        ];

        // list both (paginated), merge + de-dup
        const lists = await Promise.all(
          prefixes.map(Prefix => this.listPrefix(client, Prefix))
        );
        const allKeys = [...new Set(lists.flat())];

        // exclude just the JSON metadata object (no trailing slash)
        const metaKey = `private/${identityId}/deposits/${depositId}`;
        const fileKeys = allKeys.filter(k => k !== metaKey && !k.endsWith('/'));

        if (!fileKeys.length) {
          this.depositFiles = [];
          this.$emit('stats', { count: 0, totalSize: 0 });
          return;
        }

        // Head each file to pull metadata; build clean rows for the table
        const rows = await Promise.all(fileKeys.map(async (Key) => {
          try {
            const head = await client.send(new HeadObjectCommand({ Bucket: config.bucketName, Key }));
            const meta = head.Metadata || {};
            const nameFromKey = decodeURIComponent((Key.split('/').pop() || ''));
            const originalfilename = meta.originalfilename
              ? decodeURIComponent(meta.originalfilename)
              : nameFromKey;
            const originalpath = meta.originalpath ? decodeURIComponent(meta.originalpath) : '';
            const folder = originalpath ? originalpath.split('/').slice(0, -1).join('/') : '';
            return {
              Key,
              originalfilename,
              originalpath,
              folder,
              checksum: meta.checksum || '',
              Size: head.ContentLength || 0,
              LastModified: head.LastModified instanceof Date
                ? head.LastModified.toISOString()
                : (head.LastModified ? new Date(head.LastModified).toISOString() : ''),
            };
          } catch (e) {
            console.warn('headObject failed for', Key, e);
            return {
              Key,
              originalfilename: decodeURIComponent((Key.split('/').pop() || '')),
              originalpath: '',
              folder: '',
              checksum: '',
              Size: 0,
              LastModified: '',
            };
          }
        }));
        this.depositFiles = rows;
        this.$emit('stats', {
          count: rows.length,
          totalSize: rows.reduce((sum, f) => sum + (f.Size || 0), 0),
        });
      } catch (err) {
        console.error('Error while listing objects', err);
        this.depositFiles = [];
        }
    },

    async deleteSelected() {
      const count = this.selected.length;
      if (!confirm(`Delete ${count} selected file${count !== 1 ? 's' : ''}? This cannot be undone.`)) return;

      this.deleting = true;
      try {
        const creds = await Auth.currentCredentials();
        const client = new S3Client({
          region: config.region,
          credentials: Auth.essentialCredentials(creds),
          forcePathStyle: true,
        });
        await Promise.all(
          this.selected.map(key =>
            client.send(new DeleteObjectCommand({ Bucket: config.bucketName, Key: key }))
          )
        );
        const deletedKeys = new Set(this.selected);
        this.depositFiles = this.depositFiles.filter(f => !deletedKeys.has(f.Key));
        this.selected = [];
        this.$emit('stats', {
          count: this.depositFiles.length,
          totalSize: this.depositFiles.reduce((sum, f) => sum + (f.Size || 0), 0),
        });
      } catch (err) {
        console.error('Bulk delete failed:', err);
        emit('refresh', true);
      } finally {
        this.deleting = false;
      }
    },

    async deleteItem(item) {
      if (!item?.Key) return;

      const displayName = item.originalfilename || decodeURIComponent((item.Key.split('/').pop() || ''));

      if (!confirm(`Delete "${displayName}"? This cannot be undone.`)) return;

      try {
        const creds = await Auth.currentCredentials();
        const client = new S3Client({
          region: config.region,
          credentials: Auth.essentialCredentials(creds),
          forcePathStyle: true,
        });
        await client.send(new DeleteObjectCommand({
          Bucket: config.bucketName,
          Key: item.Key,
        }));
        this.depositFiles = this.depositFiles.filter(f => f.Key !== item.Key);
        emit('refresh', true);
        alert(`Deleted: ${displayName}`);
      } catch (err) {
        console.log(`Unable to delete ${item.Key}:`, err);
      }
    }
  }
}
</script>

